import { createServerFn } from "@tanstack/react-start";

import { bavimailBackendJson } from "@/lib/bavimail-api.server";
import type {
  BavimailApiKey,
  BavimailApiKeyListResponse,
  SerializableJson,
} from "@/features/api-keys/types";

function passthroughValidator<T>(value: T) {
  return value;
}

function normalizeApiKey(raw: Partial<BavimailApiKey> & Record<string, unknown>): BavimailApiKey {
  const now = new Date().toISOString();
  const id = String(raw.id ?? raw.keyId ?? raw.apiKeyId ?? "");
  const prefix =
    typeof raw.prefix === "string"
      ? raw.prefix
      : typeof raw.start === "string"
        ? raw.start
        : typeof raw.key === "string"
          ? raw.key.slice(0, 12)
          : null;

  return {
    id,
    name: typeof raw.name === "string" ? raw.name : null,
    start: typeof raw.start === "string" ? raw.start : prefix,
    prefix,
    key: typeof raw.key === "string" ? raw.key : undefined,
    userId: typeof raw.userId === "string" ? raw.userId : "",
    enabled: typeof raw.enabled === "boolean" ? raw.enabled : true,
    expiresAt: typeof raw.expiresAt === "string" ? raw.expiresAt : null,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : now,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : now,
    lastRequest: typeof raw.lastRequest === "string" ? raw.lastRequest : null,
    remaining: typeof raw.remaining === "number" ? raw.remaining : null,
    rateLimitEnabled: typeof raw.rateLimitEnabled === "boolean" ? raw.rateLimitEnabled : false,
    rateLimitTimeWindow:
      typeof raw.rateLimitTimeWindow === "number" ? raw.rateLimitTimeWindow : null,
    rateLimitMax: typeof raw.rateLimitMax === "number" ? raw.rateLimitMax : null,
    requestCount: typeof raw.requestCount === "number" ? raw.requestCount : null,
    refillInterval: typeof raw.refillInterval === "number" ? raw.refillInterval : null,
    refillAmount: typeof raw.refillAmount === "number" ? raw.refillAmount : null,
    lastRefillAt: typeof raw.lastRefillAt === "string" ? raw.lastRefillAt : null,
    metadata:
      raw.metadata && typeof raw.metadata === "object"
        ? (raw.metadata as Record<string, SerializableJson>)
        : null,
    permissions: Array.isArray(raw.permissions)
      ? raw.permissions.filter((permission): permission is string => typeof permission === "string")
      : null,
  };
}

function normalizeApiKeysResponse(payload: unknown) {
  if (Array.isArray(payload)) {
    return payload.map((key) => normalizeApiKey(key as Record<string, unknown>));
  }

  if (payload && typeof payload === "object") {
    const objectPayload = payload as Partial<BavimailApiKeyListResponse> & {
      keys?: unknown;
      data?: unknown;
    };
    const keys = objectPayload.apiKeys ?? objectPayload.keys ?? objectPayload.data;
    if (Array.isArray(keys)) {
      return keys.map((key) => normalizeApiKey(key as Record<string, unknown>));
    }
  }

  return [];
}

export const getApiKeysView = createServerFn({ method: "GET" }).handler(async () => {
  const result = await bavimailBackendJson<unknown>({
    path: "/api-keys",
    method: "GET",
  });

  if (result.error) {
    throw new Error(result.error.message || "Failed to load API keys.");
  }

  return normalizeApiKeysResponse(result.data);
});

export const createApiKeyAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ name?: string }>)
  .handler(async ({ data }) => {
    const result = await bavimailBackendJson<unknown>({
      path: "/api-keys",
      method: "POST",
      json: { name: data.name || undefined },
    });

    if (result.error || !result.data) {
      throw new Error(result.error?.message || "Failed to create API key.");
    }

    return normalizeApiKey(result.data as Record<string, unknown>);
  });

export const deleteApiKeyAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ keyId: string }>)
  .handler(async ({ data }) => {
    const result = await bavimailBackendJson<unknown>({
      path: `/api-keys/${encodeURIComponent(data.keyId)}`,
      method: "DELETE",
    });

    if (result.error) {
      throw new Error(result.error.message || "Failed to revoke API key.");
    }

    return { keyId: data.keyId };
  });
